import React from 'react';
import forgeFrontendClient from '@appsvio/jira-rest-api-client/src/forgeFrontendClient';
import type { User } from '@appsvio/jira-rest-api-client/src/generated-client/models/User';
import { router } from '@forge/bridge';

import { OptionItemObject } from '../../../../../../src/shared_models/interfaces';
import { GROUP_NAME_TO_INDEX, UNASSIGNED } from '../constants';
import { AccountIdDisplayName, AggregatedReport } from '../model';

export type Content = {
    [key: string]: number | string;
    levelName: string;
};

export type Item = {
    content: Content;
    hasChildren: boolean;
    id: string;
    children?: Item[];
};

const ACCOUNT_ID_PREFIX_LEN = 11;
const MAX_RESULTS = 200;
const MAX_ACCOUNT_ID_PARAMS_LEN = 6000;

const createRoute = (levelNames: string[], issueId: string, groupingOption: string, jql: string, fieldName: string) => {
    return () => router.open(encodeURI(buildJQL(levelNames, issueId, groupingOption, fieldName, jql)));
};

const buildJQL = (levelNames: string[], groupingValue: string, groupingOption: string, fieldName: string, jql?: string) =>
    '/issues/?jql=' +
    `"${fieldName}" IS NOT EMPTY ` +
    `AND ${groupingOption} = ${groupingValue === UNASSIGNED ? 'NULL' : `'${groupingValue}'`} ` +
    `AND ${getLevelNameConditions(levelNames, fieldName)} ` +
    (jql ? `AND ${jql}` : '');

const getLevelNameConditions = (levelNames: string[], fieldName: string) => {
    return levelNames.map((levelName, index) => `"${fieldName}.level${index + 1}" = '${levelName}'`).join(' AND ');
};

export const toLink = (
    levelNames: string[],
    groupingOption: string,
    jql: string,
    fieldName: string,
    value: number,
    groupingValue: string
) => (
    <a
        onClick={createRoute(levelNames, groupingValue, groupingOption, jql, fieldName)}
        href='#'
    >
        {value}
    </a>
);

export const prepareViewData = (
    fieldTreeData: OptionItemObject[],
    aggregatedData: AggregatedReport,
    groupBy: string,
    fieldName: string,
    jql: string,
    columns: string[],
    hideEmptyRows: boolean,
    levelNames: string[] = []
) =>
    fieldTreeData.reduce((previousValue, currentValue) => {
        if (
            hideEmptyRows &&
            !(
                aggregatedData[currentValue.key] &&
                Object.keys(aggregatedData[currentValue.key][GROUP_NAME_TO_INDEX[groupBy]]).length
            )
        )
            return previousValue;

        previousValue.push({
            id: currentValue.key,
            content: {
                levelName: currentValue.value,
                ...prepareSingleRowData(aggregatedData, currentValue, groupBy, fieldName, jql, columns, [
                    ...levelNames,
                    currentValue.value,
                ]),
            },
            hasChildren: currentValue.children && currentValue.children.length > 0,
            children:
                currentValue.children &&
                prepareViewData(currentValue.children, aggregatedData, groupBy, fieldName, jql, columns, hideEmptyRows, [
                    ...levelNames,
                    currentValue.value,
                ]),
        });
        return previousValue;
    }, [] as Item[]);

const prepareSingleRowData = (
    aggregatedData: AggregatedReport,
    currentValue: OptionItemObject,
    groupBy: string,
    fieldName: string,
    jql: string,
    columns: string[],
    levelNames: string[]
) =>
    aggregatedData[currentValue.key] ?
        Object.fromEntries(
            columns.map(header => {
                const values = Object.entries(aggregatedData[currentValue.key][GROUP_NAME_TO_INDEX[groupBy]]).find(
                    ([key]) => key === header
                );
                return [header, toLink(levelNames, groupBy, jql, fieldName, values ? values[1] : 0, header)];
            })
        )
    :   Object.fromEntries(
            columns.map(header => {
                return [header, toLink(levelNames, groupBy, jql, fieldName, 0, header)];
            })
        );

export const getAllUsersByAccountIds = async (accountIds: string[]) => {
    const chunks = chunkAccountIdsToLimits(accountIds);

    const users: Array<User | null> = [];
    for (const chunk of chunks) {
        const response = await forgeFrontendClient.getUsersApi().bulkGetUsers({ accountId: chunk, maxResults: MAX_RESULTS });
        users.push(...(response.values ?? []));
    }

    return Object.fromEntries(
        users
            .filter(user => user)
            .map(user => user as User)
            .map(user => [user.accountId, user.displayName])
    ) as AccountIdDisplayName;
};

const chunkAccountIdsToLimits = (accountIds: string[]) => {
    const chunks: string[][] = [[]];

    accountIds.forEach(accountId => {
        let lastChunk = chunks[chunks.length - 1];
        if (
            lastChunk.length > MAX_RESULTS ||
            lastChunk.join('').length + lastChunk.length * ACCOUNT_ID_PREFIX_LEN >= MAX_ACCOUNT_ID_PARAMS_LEN
        ) {
            lastChunk = [accountId];
            chunks.push(lastChunk);
        } else {
            lastChunk.push(accountId);
        }
    });

    return chunks;
};
